import React from 'react';
import {
  AlertTriangle,
  ExternalLink,
  FileText,
  Mail,
  ShieldCheck,
  Truck,
  Wrench
} from 'lucide-react';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { PageHero } from '../components/shared/PageHero';
import { Button } from '../components/ui/button';
import { useSEO } from '../hooks/useSEO';

const summaryCards = [
  {
    title: 'Cobertura general',
    value: '3 años',
    description: 'Garantía de reparación desde la fecha de la primera compra.'
  },
  {
    title: 'Baterías',
    value: '1 año / 3.000 km',
    description: 'Se aplica el límite que antes se alcance.'
  },
  {
    title: 'Accesorios',
    value: '6 meses',
    description: 'Deben entregarse completos y con la caja original en buen estado.'
  },
  {
    title: 'Seminuevos',
    value: '1 año',
    description: 'Las devoluciones solo se aceptan durante los cinco días posteriores a la recepción.'
  }
];

const generalTerms: React.ReactNode[] = [
  'Zamelux ofrece garantía de reparación durante un período de 3 años a partir de la fecha de la primera compra del producto, en caso de no ser posible, reposición o sustitución de cualquier aparato, pieza o componente que presente fallos de funcionamiento por defecto de fabricación, siempre justificando la compra debidamente mediante la factura o ticket de compra.',
  'La reparación o el cambio de producto no prorroga el periodo de garantía inicial.',
  (
    <>
      Los productos remitidos al Servicio Técnico que no presenten averías tras su revisión y diagnóstico
      tendrá un cargo mínimo por tiempo empleado más gastos de transporte (
      <a
        href="/pdf/Explicacion-Precios-de-Garantia.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-black underline underline-offset-4"
      >
        Información tarifas
      </a>
      ).
    </>
  ),
  (
    <>
      Los productos remitidos al Servicio Técnico de los cuales el cliente rechace la reparación también
      tendrá un cargo mínimo por tiempo empleado más gastos de transporte (
      <a
        href="/pdf/Explicacion-Precios-de-Garantia.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-black underline underline-offset-4"
      >
        Información tarifas
      </a>
      ).
    </>
  ),
  'Los productos enviados a reparación y queden estacionados sin respuesta del cliente, generarán gastos de almacenaje a partir del octavo día a contar desde el envío del presupuesto (Gasto almacenaje 1€/día).',
  'El producto deberá enviarse en su embalaje original en perfecto estado con todas las etiquetas y adhesivos en perfecto estado junto al ticket o factura de compra. No nos hacemos responsables en accesorios o partes compradas a Zamelux y que se hayan añadido a posteriori.',
  'Servicio técnico se reserva el derecho de sustituir el aparato defectuoso por otro de similares características que el recibido, independientemente del precio que tuviera el modelo a reparar en el momento que el cliente realizase la compra inicial.',
  'Los accesorios cuentan con una garantía de 6 meses (Deberán presentarse con todos sus componentes, caja original en buen estado).',
  'El producto cuenta con 3 años de garantía exceptuando las baterías, que cuentan con 1 año de garantía o 3.000km, lo que antes ocurra. Además, las baterías son objeto de desgaste y pérdida de capacidad por el uso regular, que no están cubiertos por la garantía.',
  'El desgaste por el uso de piezas no está cubierto por la garantía (Pastillas de freno, guardabarros, etc.).',
  'La garantía solo se aplica exclusivamente en productos Zamelux.'
];

const shippingTerms = [
  'Los gastos de envío estarán cubiertos por Zamelux en garantías procedentes de direcciones dentro de Península y Baleares.',
  'Resto de países de Europa: se harán cargo de los gastos de envío tanto de ida como de vuelta.',
  'Islas Canarias, Ceuta y Melilla: se harán cargo de los gastos de envío tanto de ida como de vuelta.'
];

const warrantyVoidTerms = [
  'El cliente no adjunta el justificante de compra junto con el terminal/producto.',
  'Rotura o manipulación de los precintos de garantía.',
  'Material dañado, sin embalaje adecuado y/o daños de transporte.',
  'Reparación no autorizada, mal uso, colisión, negligencia, abuso, accidente, alteración uso indebido de accesorios que no sean del producto o rotura, etiqueta de garantía alterada.',
  'Los productos dañados por averías producidas por causas ajenas al aparato (fuego, agua, subidas de tensión eléctrica, uso en condiciones extremas de temperatura y(o humedad etc.) quedan excluidos de garantía.',
  'La garantía no cubre daños en el neumático como pueden ser desgastes, pinchazos, reventones etc.',
  'No atender a las recomendaciones e instrucciones de uso.',
  'Cuando la información original en la factura muestre claros signos de haber sido alterado de forma deliberada o por discrepancias entre la garantía, la prueba de compra y el producto.',
  'Golpes, deterioros causados por líquidos o sustancias corrosivas o cualquier otra anomalía atribuida directamente o indirectamente por el consumidor.',
  'Cuando el producto muestre señales claras de un uso incorrecto o sobrepase las capacidades del mismo.',
  'El producto haya sido modificado o reparado por personas no autorizadas.',
  'Derivado del uso del producto, muestre desgaste propio del uso del mismo.',
  'La garantía haya llegado a su fin.',
  'El sello de seguridad de la batería haya cambiado de color, signo de haber entrado líquido como puede ser agua o similares. Quedando los circuitos eléctricos y sus componentes afectados.',
  'Daños por causas mayores.',
  'Artículos dañados por sobretensiones o sobreintensidades eléctricas.',
  'Daños provocados por presión, torsión, exposición a humedad, exposición a temperaturas extremas, cambios bruscos de temperatura, corrosión, oxidación y, en general, productos químicos o sustancias que puedan alterar el producto.',
  'Modificaciones de los parámetros del equipo realizados por el usuario o terceros.',
  'Los dispositivos que no llevan identificado número de Nº de Serie o que éstos hayan sido alterados o borrados (incluyendo el deterioro) o modificados respecto al número expresado en el certificado de garantía o factura de compra.',
  'Los dispositivos que sean utilizados para un uso no personal. Es decir, uso comercial, alquiler, uso compartido.'
];

function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-4 text-base leading-7 text-gray-700">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-3">
          <span className="mt-3 h-2 w-2 flex-shrink-0 rounded-full bg-black" aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function WarrantyConditions() {
  useSEO({
    title: 'Condiciones de Garantía',
    description: 'Consulta cómo tramitar la garantía Zamelux, los términos generales de cobertura, las exclusiones y las condiciones para productos fuera de garantía.',
    keywords: 'garantía Zamelux, garantía Zwheel, reparación, condiciones de garantía, servicio técnico'
  });

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        tag="Legal"
        title="Condiciones de Garantía"
        subtitle="Consulta la cobertura, las exclusiones y el proceso oficial para tramitar una reparación Zamelux."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-max section-padding">
          <div className="mx-auto max-w-5xl space-y-10">
            <AnimatedSection>
              <div className="rounded-[2rem] border border-gray-200 bg-[color:var(--zw-ivory)] p-8 md:p-10">
                <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-3xl">
                    <span className="mb-3 block text-sm font-bold uppercase tracking-[0.18em] text-[color:var(--zw-moss)]">
                      Tramitación
                    </span>
                    <h2 className="mb-4 text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
                      ¿Dónde puedo tramitar la garantía?
                    </h2>
                    <p className="text-lg leading-8 text-gray-700">
                      La tramitación de la garantía de productos Zamelux se realizará a través del enlace
                      oficial de reparaciones.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                    <Button asChild className="bg-black text-white hover:bg-gray-800">
                      <a
                        href="https://reparacion.zamelux.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Tramitar garantía
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                    <Button asChild variant="outline">
                      <a href="/pdf/Explicacion-Precios-de-Garantia.pdf" target="_blank" rel="noopener noreferrer">
                        Ver tarifas SAT
                        <FileText className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.05}>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {summaryCards.map((card) => (
                  <div key={card.title} className="rounded-[1.75rem] border border-gray-200 bg-white p-6 shadow-sm">
                    <p className="mb-3 text-sm font-bold uppercase tracking-[0.14em] text-gray-500">{card.title}</p>
                    <p className="mb-3 text-3xl font-bold text-gray-900">{card.value}</p>
                    <p className="text-sm leading-6 text-gray-600">{card.description}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.1}>
              <div className="rounded-[2rem] border border-gray-200 bg-white p-8 md:p-10">
                <div className="mb-8 flex items-center gap-3">
                  <ShieldCheck className="h-7 w-7 text-black" />
                  <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">Términos y Condiciones Generales</h2>
                </div>
                <BulletList items={generalTerms} />
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.15}>
              <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="rounded-[2rem] border border-gray-200 bg-white p-8 md:p-10">
                  <div className="mb-6 flex items-center gap-3">
                    <Wrench className="h-7 w-7 text-black" />
                    <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">Productos Seminuevos</h2>
                  </div>
                  <p className="text-base leading-7 text-gray-700">
                    Los productos seminuevos son productos en perfecto estado, procedentes de exposiciones,
                    devoluciones de cliente, demostraciones o simplemente porque el embalaje se encuentra un poco
                    deteriorado; también puede tratarse de productos ya descatalogados. Las devoluciones de estos
                    productos solo se aceptarán durante los siguiente cinco días tras la recepción del producto.
                    Estos productos cuentan con un año de garantía.
                  </p>
                </div>

                <div className="rounded-[2rem] border border-gray-200 bg-[color:var(--zw-ivory)] p-8 md:p-10">
                  <div className="mb-6 flex items-center gap-3">
                    <Truck className="h-7 w-7 text-black" />
                    <h2 className="text-2xl font-bold text-gray-900">Sobre los gastos de envío</h2>
                  </div>
                  <BulletList items={shippingTerms} />
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="rounded-[2rem] border border-gray-200 bg-white p-8 md:p-10">
                <div className="mb-4 flex items-center gap-3">
                  <AlertTriangle className="h-7 w-7 text-black" />
                  <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">Anulación de Garantía</h2>
                </div>
                <p className="mb-2 text-lg font-semibold text-gray-900">
                  Anulaciones de garantía – Productos fuera de garantía
                </p>
                <p className="mb-8 text-base leading-7 text-gray-700">
                  Hay ciertas ocasiones en las que la garantía de los productos de Zamelux/Zwheel quedará anulada,
                  y por tanto estos productos se considerarán fuera de garantía. Concretamente, la garantía quedará
                  anulada en cualquiera de estos casos:
                </p>

                <ol className="space-y-4">
                  {warrantyVoidTerms.map((item, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-4 rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 text-base leading-7 text-gray-700"
                    >
                      <span className="mt-2 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-black" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.25}>
              <div className="rounded-[2rem] border border-gray-200 bg-[color:var(--zw-sand)] p-8 md:p-10">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-3xl">
                    <span className="mb-3 block text-sm font-bold uppercase tracking-[0.18em] text-[color:var(--zw-moss)]">
                      Fuera de garantía
                    </span>
                    <p className="text-xl font-bold leading-8 text-gray-900">
                      Para los productos fuera de garantía, se deberá realizar la solicitud de reparación bajo
                      presupuesto.
                    </p>
                    <p className="mt-3 text-base leading-7 text-gray-700">
                      Para ello, deberá ponerse en contacto con nosotros a través de{' '}
                      <a
                        href="mailto:info@zamelux.com"
                        className="font-semibold text-black underline underline-offset-4"
                      >
                        info@zamelux.com
                      </a>
                      .
                    </p>
                    <p className="mt-6 text-sm leading-6 text-gray-600">
                      Las piezas dañadas quedarán en posesión del SAT para su posterior reciclaje según la normativa
                      vigente.
                    </p>
                  </div>

                  <Button asChild className="bg-black text-white hover:bg-gray-800">
                    <a href="mailto:info@zamelux.com">
                      Escribir a soporte
                      <Mail className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
